import { useState } from "react";
import { Form, message } from "antd";
import { hardDeleteUser, deactivateUser, reactivateUser, removeUserWorkspace } from "../../api/userManagementApi";

export const useUserActions = (users, setUsers, selectedRows, setSelectedRows, reloadUsers) => {
  const [isInviteModalVisible, setIsInviteModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState("user"); // "user" | "workspace" | "permanent"
  const [isDeleting, setIsDeleting] = useState(false);
  const [bulkDeleteModalVisible, setBulkDeleteModalVisible] = useState(false);
  const [successModalVisible, setSuccessModalVisible] = useState(false);
  const [deletedUser, setDeletedUser] = useState(null);
  const [bulkDeletedCount, setBulkDeletedCount] = useState(0);
  const [bulkDeletedMarkets, setBulkDeletedMarkets] = useState("");
  const [deletedUsers, setDeletedUsers] = useState([]); // Store deleted users for restore
  const [isRestoringUsers, setIsRestoringUsers] = useState(false);
  const [isReactivating, setIsReactivating] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // Invite User Modal handlers
  const handleInviteUser = () => {
    setIsInviteModalVisible(true);
  };

  const handleInviteModalOk = async () => {
    try {
      await form.validateFields();
      messageApi.success("User invitation sent successfully!");
      form.resetFields();
      setIsInviteModalVisible(false);
      // Add logic to actually invite user
    } catch (error) {
      console.error("Error inviting user:", error);
    }
  };

  const handleInviteModalCancel = () => {
    setIsInviteModalVisible(false);
    form.resetFields();
  };

  // Delete User handlers. Deactivate always operates at workspace granularity
  // (or the whole account when this is the user's only workspace) — never at
  // the product/module level, since scope changes are Edit's job.
  const handleDeleteUser = (user) => {
    const type = (user.workspaceCount ?? 1) > 1 ? "workspace" : "user";
    setDeleteType(type);
    setUserToDelete(user);
    setDeleteModalVisible(true);
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;

    setIsDeleting(true);
    try {
      if (deleteType === "user") {
        // SOFT DELETE — deactivate (reversible). The user moves to the
        // Deactivated view; their sessions are revoked server-side. Restore via
        // the success-modal "Restore" or the Deactivated view's Reactivate.
        await deactivateUser(userToDelete.email);
      } else if (deleteType === "permanent") {
        // HARD DELETE — permanent purge from the Deactivated view (archived +
        // team-alerted server-side). Cannot be undone.
        await hardDeleteUser(userToDelete.email);
      } else {
        // REMOVE WORKSPACE — user has other workspaces, only remove this one
        await removeUserWorkspace(userToDelete.userId, userToDelete.client);
      }

      // Reload from API so rowSpan metadata is recalculated correctly
      if (reloadUsers) await reloadUsers();
      setSelectedRows([]);

      setDeletedUsers([userToDelete]);
      setDeletedUser(userToDelete);
      setDeleteModalVisible(false);
      setSuccessModalVisible(true);
      setUserToDelete(null);
    } catch (error) {
      console.error("Delete failed:", error);
      messageApi.error(error.message || "Failed to delete. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteUser = () => {
    setDeleteModalVisible(false);
    setUserToDelete(null);
  };

  // Bulk Delete handlers
  const handleBulkDelete = () => {
    setBulkDeleteModalVisible(true);
  };

  const confirmBulkDelete = async () => {
    const selectedUsers = users.filter((user) =>
      selectedRows.includes(user.key)
    );

    if (selectedUsers.length === 0) {
      messageApi.warning("No valid users selected for deletion.");
      setBulkDeleteModalVisible(false);
      return;
    }

    // Deduplicate by userId so we call deactivateUser once per user
    const uniqueUsers = [
      ...new Map(selectedUsers.map((u) => [u.userId, u])).values(),
    ];

    setIsDeleting(true);
    try {
      // Bulk delete from the Active view deactivates (reversible), matching the
      // single-row Delete semantics.
      await Promise.all(uniqueUsers.map((u) => deactivateUser(u.email)));

      if (reloadUsers) await reloadUsers();

      setDeletedUsers(uniqueUsers);
      setBulkDeletedCount(uniqueUsers.length);
      setSelectedRows([]);
      setBulkDeleteModalVisible(false);
      setSuccessModalVisible(true);
    } catch (error) {
      console.error("Bulk delete failed:", error);
      messageApi.error(error.message || "Failed to delete. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelBulkDelete = () => {
    setBulkDeleteModalVisible(false);
  };

  // Restore Users handler — the success-modal "Restore" affordance after a
  // soft delete. Calls the reactivate endpoint per user, so the rows and the
  // backend cannot diverge.
  const handleRestoreUsers = async () => {
    if (deletedUsers.length === 0) return;

    setIsRestoringUsers(true);

    try {
      await Promise.all(deletedUsers.map((u) => reactivateUser(u.email)));

      // Reload from API so the restored users reappear in the Active view with
      // correct rowSpan metadata.
      if (reloadUsers) await reloadUsers();

      messageApi.success(
        deletedUsers.length === 1
          ? `${deletedUsers[0].name} has been restored successfully!`
          : `${deletedUsers.length} users have been restored successfully!`
      );

      // Clear state and close modal
      handleSuccessModalClose();
    } catch (error) {
      console.error("Failed to restore users:", error);
      messageApi.error(
        error.message || "Failed to restore users. Please try again."
      );
    } finally {
      setIsRestoringUsers(false);
    }
  };

  // Reactivate a single deactivated user — the Deactivated-view row action.
  const handleReactivateUser = async (user) => {
    if (!user?.email) return;
    setIsReactivating(true);
    try {
      await reactivateUser(user.email);
      if (reloadUsers) await reloadUsers();
      setSelectedRows([]);
      messageApi.success(`${user.name} has been reactivated.`);
    } catch (error) {
      console.error("Reactivate failed:", error);
      messageApi.error(error.message || "Failed to reactivate user.");
    } finally {
      setIsReactivating(false);
    }
  };

  // Permanently delete a deactivated user — the Deactivated-view "Delete"
  // action. Routes through the shared confirmation modal with the "permanent"
  // type so confirmDeleteUser calls hardDeleteUser.
  const handlePermanentDelete = (user) => {
    setDeleteType("permanent");
    setUserToDelete(user);
    setDeleteModalVisible(true);
  };

  const handleSuccessModalClose = () => {
    setSuccessModalVisible(false);
    setDeletedUser(null);
    setBulkDeletedCount(0);
    setBulkDeletedMarkets("");
    setDeletedUsers([]); // Clear deleted users when modal closes
  };

  // Export handler
  const handleExport = () => {
    messageApi.success("User data exported successfully!");
    // Add logic to actually export data (CSV, Excel, etc.)
  };

  return {
    // Invite Modal
    isInviteModalVisible,
    handleInviteUser,
    handleInviteModalOk,
    handleInviteModalCancel,
    form,

    // Delete Modal
    deleteModalVisible,
    userToDelete,
    deleteType,
    isDeleting,
    handleDeleteUser,
    confirmDeleteUser,
    cancelDeleteUser,

    // Deactivated-view actions
    handleReactivateUser,
    handlePermanentDelete,
    isReactivating,

    // Bulk Delete Modal
    bulkDeleteModalVisible,
    handleBulkDelete,
    confirmBulkDelete,
    cancelBulkDelete,

    // Success Modal
    successModalVisible,
    deletedUser,
    bulkDeletedCount,
    bulkDeletedMarkets,
    deletedUsers,
    isRestoringUsers,
    handleRestoreUsers,
    handleSuccessModalClose,

    // Export
    handleExport,

    // Message API
    messageApi,
    contextHolder
  };
};
